import { Typography } from "@mui/joy";
import { Box, Button, Stack } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useConfig } from "../../../context/ConfigContext";
import AllergenChip from "../../../images/icons/AllergenChip";
import { ApiResponse } from "../../../interfaces/ApiResponse";
import {
  CATEGORY_ITEM_TYPE,
  CategoryItem,
  GroupedArticleByLevel,
  Ingredient,
  MenuItem,
  OrderItem,
  Restaurant,
  SelectedIngredient,
} from "../../../interfaces/Catalogue";
import { catalogueApi } from "../../../services/catalogueApi";
import { updateCart } from "../../../store/cart/cartActions";
import { getIcon, IconType } from "../../../theme/icons";
import {
  getCategoryItemType,
  groupMenuItemsByLevel,
} from "../../../utils/menuUtils";
import {
  calculateArticleTotalPriceWithOffers,
  calculateMenuFinalPrice,
  findPriceByTypeOrder,
  getPriceByTypeOrderForComposition,
} from "../../../utils/priceUtils";
import DeliveryModeConflictAlert from "../../shared/Modals/DeliveryModeConflictAlert/DeliveryModeConflictAlert";
import LevelSkeleton from "../../shared/Skeletons/LevelSkeleton/LevelSkeleton";
import IngredientList from "../Ingredient/IngredientList/IngredientList";
import LevelList from "../Level/LevelList/LevelList";
import "./OrderInfo.scss";
import { tagFreeItems } from "../../../utils/loyaltyUtils";
import { RootState } from "../../../store/rootState";

type ScrollToLevelFunction = (levelId: number) => void;

interface OrderInfoProps {
  categoryId: string | undefined;
  categoryItemType: string | undefined;
  categoryItem: CategoryItem;
  restaurant: Restaurant;
  // priceByTypeOrder: number;
}

const OrderInfo = (props: OrderInfoProps) => {
  const { categoryItemType, categoryItem, categoryId, restaurant } =
    props;
  const location = useLocation();
  const parentMenusIds = location.state?.parentMenusIds || [];
  const { config } = useConfig();
  const selectedMode = useSelector(
    (state: RootState) => state.app.selectedMode
  );
  const { isMenu, isArticle } = getCategoryItemType(String(categoryItemType));
  const scrollToLevelRef = useRef<((index: number) => void) | null>(null);
  const priceByTypeOrder = findPriceByTypeOrder(
    categoryItem?.typeOrderCategory,
    selectedMode
  );
  const stackRef = useRef<HTMLDivElement | null>(null);
  const remiseMessage =
    categoryItem.rewardDiscountType === 0
      ? `${categoryItem.rewardDiscountValue}%`
      : `${categoryItem.rewardDiscountValue?.toFixed(2)} €`;

  const [unitPrice, setUnitPrice] = useState<number>(priceByTypeOrder || 0);
  const [totalPrice, setTotalPrice] = useState(unitPrice);
  const [finalUnitPrice, setFinalUnitPrice] = useState(unitPrice);
  const [totalIngredientCost, setTotalIngredientCost] = useState(0);

  const [levels, setLevels] = useState<GroupedArticleByLevel[]>([]);
  const [showPanierAlert, setShowPanierAlert] = useState(false);
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [selectedIngredients, setSelectedIngredient] = useState<
    SelectedIngredient[]
  >([]); // FOR ARTICLE
  const [eliminatedIngredients, setEliminatedIngredients] = useState<
    SelectedIngredient[]
  >([]);
  const [articleCount, setArticleCount] = useState<number>(1);
  const [openAllg, setOpenAllg] = useState<boolean>(false);
  const [incompleteLevel, setIncompleteLevel] = useState<number>();
  const [loading, setLoading] = useState<boolean>(false);
  const [order, setOrder] = useState<OrderItem[]>([]);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const dispatch = useDispatch();
  const restaurantCarts = useSelector(
    (state: any) => state?.cart?.restaurantCarts
  );
  const navigate = useNavigate();

  const message = `${remiseMessage} offerts pour ${categoryItem.rewardTotalPtRequired} points`;
  const purchaseOffersMessage = ` ${categoryItem.purchaseOffers?.nbPurchased} acheté(s) = ${categoryItem.purchaseOffers?.nbOffered} offert(s)`;

  const hasRewardDiscount = !!categoryItem.rewardDiscountValue;
  const hasPurchaseOffers = !!categoryItem.purchaseOffers;

  const combinedMessage = `🎁 ${hasRewardDiscount ? message : ""
    }${hasRewardDiscount && hasPurchaseOffers ? " / " : ""}${
    // }${hasRewardDiscount && hasPurchaseOffers ? " • / " : ""}${
    hasPurchaseOffers ? purchaseOffersMessage : ""
    }`;

  const onOrderGenerated = (orderItem: OrderItem) => {
    setOrder((prevItems) => {
      // Filter out existing items with the same level.id
      const updatedItems = prevItems?.filter(
        (item) => item?.level?.id !== orderItem?.level?.id
      );

      const newOrder = [...updatedItems, orderItem];

      // Taged free Selected Articles
      const updatedOrder = tagFreeItems(newOrder, selectedMode);

      // Add the new orderItem
      return newOrder;
    });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (isMenu) {
      setFinalUnitPrice(calculateMenuFinalPrice(order, selectedMode));
    }
  }, [order]);

  useEffect(() => {
    if (categoryItemType == CATEGORY_ITEM_TYPE.MENU) {
      fetchLevels();
    }

    if (categoryItemType == CATEGORY_ITEM_TYPE.ARTICLE) {
      fetchIngredient(Number(categoryItem.idRef));
    }
  }, []);

  useEffect(() => {
    if (categoryItemType == CATEGORY_ITEM_TYPE.MENU) {
      fetchLevels();
    }

    if (categoryItemType == CATEGORY_ITEM_TYPE.ARTICLE) {
      fetchIngredient(Number(categoryItem.idRef));
    }
  }, [parentMenusIds]);

  useEffect(() => {
    if (isMenu) {
      const totalPrice = (finalUnitPrice || 0) * articleCount;
      const formattedTotalPrice = parseFloat(totalPrice.toFixed(2));
      setTotalPrice(formattedTotalPrice);
    } else if (isArticle) {
      const finalTotalPrice = calculateArticleTotalPriceWithOffers(
        categoryItem.purchaseOffers,
        finalUnitPrice,
        articleCount
      );
      const formattedTotalPrice = parseFloat(finalTotalPrice.toFixed(2));

      setTotalPrice(formattedTotalPrice);
    }
  }, [articleCount, finalUnitPrice]);

  useEffect(() => {
    if (levels[0]?.menuItems?.length == 1) {
      setUnitPrice(
        findPriceByTypeOrder(levels[0].menuItems[0].typeOrderPrices, selectedMode)
      );
    }
  }, [levels]);

  const fetchLevels = async () => {
    try {
      setLoading(true);

      const response: { data: ApiResponse<MenuItem[]> } =
        await catalogueApi.GetLevelsByMenuId(
          categoryId,
          Number(categoryItem.id),
          parentMenusIds.join(","),
          restaurant?.id
        );
      const result = response?.data?.data?.result;
      const groupedLevels = groupMenuItemsByLevel(result, selectedMode);

      setLevels(groupedLevels);
    } catch (error: any) {
      console.error("🚀 ~ fetchCategoryDetails ~ error:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchIngredient = async (articleId: number) => {
    try {
      setLoading(true);
      const response: { data: ApiResponse<Ingredient[]> } =
        await catalogueApi.GetArticleComposition(
          articleId,
          false,
          restaurant?.id
        );
      setIngredients(response?.data?.data?.result);
    } catch (error: any) {
      console.error("🚀 ~ fetchCategoryDetails ~ error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryCount = (type: string) => {
    if (type === "INCREASE") {
      setArticleCount((prevCount) => prevCount + 1);
    } else if (type === "DECREASE" && articleCount > 1) {
      setArticleCount((prevCount) => prevCount - 1);
    }
  };

  const handleClickAddToCart = async () => {
    if (isAddingToCart) return; // Prevent multiple clicks if already adding to cart

    setIsAddingToCart(true);
    try {
      await addToCart();
    } catch (error) {
      console.error("Error adding to cart:", error);
    } finally {
      setIsAddingToCart(false); // Re-enable button after processing
    }
  };

  const findIncompleteLevel = (
    order: OrderItem[],
    scrollToLevel: ScrollToLevelFunction
  ): OrderItem | null => {
    const mandatoryLevels = order?.filter(
      ({ level }) => level?.niveauMinSelection > 0
    );

    const incompleteLevel = mandatoryLevels?.find(
      ({ selectedArticles, level }) => {
        const niveauSelectionCount = selectedArticles.reduce(
          (prev, article) => prev + (article?.selectedMenuItem?.quantity || 0),
          0
        );
        return niveauSelectionCount < level.niveauMinSelection;
      }
    );

    if (incompleteLevel) {
      const { level } = incompleteLevel;
      scrollToLevel(level?.id);
      setIncompleteLevel(level?.id);
      return incompleteLevel;
    }
    return null;
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setIncompleteLevel(0);
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [incompleteLevel]);

  const addToCart = async () => {
    if (isMenu && scrollToLevelRef.current) {
      const incompleteLevel = findIncompleteLevel(
        order,
        scrollToLevelRef.current
      );
      if (incompleteLevel) {
        return;
      }
    }
    if (
      restaurantCarts[restaurant?.id] &&
      restaurantCarts[restaurant?.id]?.mode !== selectedMode
    ) {
      setShowPanierAlert(true);
      return;
    }

    handleConfirmOrder();
  };

  const handleConfirmOrder = () => {
    if (showPanierAlert) {
      setShowPanierAlert(false);
    }

    if (isMenu) {
      categoryItem.order = order;
    }

    if (isArticle) {
      categoryItem.selectedIngredients = selectedIngredients;
      categoryItem.eliminatedIngredients = eliminatedIngredients;
    }

    categoryItem.quantity = articleCount;
    categoryItem.unitPrice = unitPrice;
    categoryItem.finalUnitPrice = finalUnitPrice;
    categoryItem.totalIngredientCost = totalIngredientCost;

    let ordersOfcurrentRestaurant: OrderItem[] =
      restaurantCarts[restaurant?.id]?.categories &&
        restaurantCarts[restaurant?.id]?.mode === selectedMode
        ? [...restaurantCarts[restaurant?.id]?.categories, categoryItem]
        : [categoryItem];

    let orders = restaurantCarts;

    orders[restaurant?.id] = {
      restaurant: restaurant,
      categories: ordersOfcurrentRestaurant,
      mode: selectedMode,
      menuId: Number(categoryId),
    };

    dispatch(updateCart(orders));
    localStorage.setItem("redirectedToPrevPage", "true");
    navigate(`/restaurant/${restaurant?.id}`, {
      state: { mode: selectedMode },
      replace: true,
    });
  };

  const handleCancelOrder = () => {
    setShowPanierAlert(false);
  };

  const handleClickAllergen = () => {
    setOpenAllg(true);
  };

  const updateSelectedIngredients = (
    selectedIngredients: SelectedIngredient[]
  ) => {
    let ingredientCost = 0;

    selectedIngredients?.map((ingredient) => {
      const priceIngredient = getPriceByTypeOrderForComposition(
        selectedMode,
        ingredient?.ingredient
      );
      ingredientCost += priceIngredient * ingredient.quantity;
    });

    const inclusIngredient = ingredients.filter(
      (ingredient) =>
        ingredient.inclus &&
        !selectedIngredients?.some((selected) => selected.id === ingredient.id)
    );

    setFinalUnitPrice(
      parseFloat((ingredientCost + priceByTypeOrder).toFixed(2))
    );

    setTotalIngredientCost(parseFloat(ingredientCost.toFixed(2)));

    setSelectedIngredient(selectedIngredients);
    setEliminatedIngredients(
      inclusIngredient.map((ingredient) => ({
        id: ingredient.id,
        ingredient,
        quantity: 0,
      }))
    );
  };

  return (
    <div id="OrderInfo">
      {/* Category Info Section */}
      <Stack className="mainMenuInfos" direction={"column"} spacing={2}>
        <Stack direction={"column"}>
          <p className="name-cat">{categoryItem?.designation}</p>
          {(hasRewardDiscount || hasPurchaseOffers) && (
            <p className="loyaltyMessage">{combinedMessage} </p>
          )}
          <p className="unit-price">
            {unitPrice !== 0 ? `${unitPrice?.toFixed(2)} €` : "\u00A0"}
          </p>
        </Stack>

        <p className="description">{categoryItem?.description} </p>

        {/* Quantity Section */}
        <div className="qtyManagerSection">
          <Box>
            <Stack
              className="qtyManager"
              direction="row"
              spacing={2}
              justifyContent="flex-start"
            >
              {getIcon(IconType.MINUS, {
                color: config?.primaryColor,
                onClick: () => handleCategoryCount("DECREASE"),
              })}

              <Typography
                style={{
                  alignSelf: "center",
                  fontWeight: "700",
                  color: "background: #747474;",
                  fontSize: "14px",
                }}
              >
                {articleCount}
              </Typography>
              {getIcon(IconType.PLUS, {
                color: config?.primaryColor,
                onClick: () => handleCategoryCount("INCREASE"),
              })}
              {/* <img
                onClick={() => {
                  handleCategoryCount("INCREASE");
                }}
                src={PlusboxOrangeIcon}
                alt="PlusboxOrangeIcon"
              /> */}
            </Stack>
          </Box>
        </div>

        <div>
          <Stack spacing={1} flex={1} direction={"row"}>
            {categoryItem?.hasAllergene && (
              <AllergenChip onClick={handleClickAllergen} />
            )}
          </Stack>
        </div>
      </Stack>

      {isMenu && (
        <Stack className="menuProducts" spacing={2} ref={stackRef}>
          {loading ? (
            Array.from({ length: categoryItem.menuLevelsCount })?.map(
              (_, index) => <LevelSkeleton key={index} />
            )
          ) : (
            <LevelList
              levels={levels}
              onOrderGenerated={onOrderGenerated}
              scrollToLevel={scrollToLevelRef}
              incompleteLevel={incompleteLevel || 0}
            />
          )}
        </Stack>
      )}

      {isArticle && (
        <Stack
          className="menuProducts"
          spacing={2}
          ref={stackRef}
          style={{ marginBottom: "36px", marginTop: "20px" }}
        >
          <IngredientList
            ingredients={ingredients}
            updateSelectedIngredients={updateSelectedIngredients}
            compositionMaxSelection={categoryItem?.maxNbrSelectionComposition}
          />
        </Stack>
      )}

      {!loading && (
        <Button
          className="customFilled btnAddToCart"
          variant="contained"
          sx={{ display: { xs: "none", md: "block" } }}
          disabled={isAddingToCart}
          onClick={handleClickAddToCart}
        >
          Ajouter {articleCount} au panier {totalPrice > 0 && `${totalPrice}€`}
        </Button>
      )}

      <div className="FooterContainer">
        {!loading && (
          <Button
            className="customFilled btnAddToCart"
            variant="contained"
            sx={{ display: { xs: "block", md: "none" } }}
            onClick={handleClickAddToCart}
          >
            Ajouter {articleCount} au panier{" "}
            {totalPrice > 0 && `${totalPrice}€`}
          </Button>
        )}
      </div>
      <DeliveryModeConflictAlert
        message="Votre panier contient déjà des articles avec un autre mode de livraison. Voulez-vous effacer les articles existants ?"
        onConfirm={handleConfirmOrder}
        onCancel={handleCancelOrder}
        isVisible={showPanierAlert}
      />
    </div>
  );
};

export default OrderInfo;
